import axios, {
  AxiosInstance,
  AxiosProgressEvent,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import * as Sentry from "@sentry/react";
import { errorCodes } from "@Helpers/errorCodes";

// ============================================================================
// Shared Types
// ============================================================================

export interface PlatformAdapter {
  storage: {
    getItem: (key: string) => Promise<string | null> | string | null;
    removeItem: (key: string) => Promise<void> | void;
  };
  navigation: {
    getCurrentPath: () => string;
    redirectToLogin: (params?: { redirectUrl?: string; errorMessage?: string }) => void;
  };
  dispatch: (action: unknown) => void;
  actions: {
    setLoggedOut: () => unknown;
    setLicenceInvalid?: () => unknown;
  };
  getRequestHeaders?: () => Promise<Record<string, string>> | Record<string, string>;
  // Whether this platform authenticates via browser cookies (default true). Native
  // mobile authenticates with a Bearer header and must set this to false: attaching
  // cookies makes the OS cookie jar replay the persistent jwtToken cookie, which
  // switches the server's CSRF enforcement on for requests that can't satisfy it
  // (the csrfToken session cookie does not survive an app restart).
  useCookieAuth?: boolean;
  excludedPathsFor401?: string[];
  // Resolves the backend origin (e.g. `${window.location.origin}/api-server`). Required so
  // internal requests like the CSRF token fetch hit the backend instead of the frontend host.
  getBackendUrl: () => string;
  // Whether the user has an authenticated session. Used only to suppress the
  // csrf-missing warning on pre-login requests the server never CSRF-checks.
  isAuthenticated?: () => boolean;
}

export interface ApiResponse<T> {
  data: T;
  message?: string;
  success?: boolean;
}

type UnspecifiedResponseType = {
  readonly __brand: "ERROR: You must specify a response type — use axiosWrapper.post<YourResponseType>(...)";
};

type NoAnyGuard<T> = 0 extends 1 & T ? [never] : [];

export interface TypedAxiosInstance {
  post<TResponse = UnspecifiedResponseType, TRequest = unknown>(
    url: string,
    data?: TRequest,
    config?: AxiosRequestConfig,
    ...noAny: NoAnyGuard<TResponse>
  ): Promise<AxiosResponse<TResponse>>;
  get<TResponse = UnspecifiedResponseType>(
    url: string,
    config?: AxiosRequestConfig,
    ...noAny: NoAnyGuard<TResponse>
  ): Promise<AxiosResponse<TResponse>>;
  put<TResponse = UnspecifiedResponseType, TRequest = unknown>(
    url: string,
    data?: TRequest,
    config?: AxiosRequestConfig,
    ...noAny: NoAnyGuard<TResponse>
  ): Promise<AxiosResponse<TResponse>>;
  delete<TResponse = UnspecifiedResponseType>(
    url: string,
    config?: AxiosRequestConfig,
    ...noAny: NoAnyGuard<TResponse>
  ): Promise<AxiosResponse<TResponse>>;
  patch<TResponse = UnspecifiedResponseType, TRequest = unknown>(
    url: string,
    data?: TRequest,
    config?: AxiosRequestConfig,
    ...noAny: NoAnyGuard<TResponse>
  ): Promise<AxiosResponse<TResponse>>;
}

// ============================================================================
// Shared Factory
// ============================================================================

const ERROR_CODES = { LICENCE_EXPIRED: 1001 } as const;
const HTTP_STATUS = { UNAUTHORIZED: 401, FORBIDDEN: 403, TOO_MANY_REQUESTS: 429 } as const;

const STATE_CHANGING_METHODS = new Set(["post", "put", "patch", "delete"]);
let cachedCsrfToken: string | null = null;
let csrfFetchInFlight: Promise<string | null> | null = null;

const CSRF_FETCH_SLOW_THRESHOLD_MS = 2500;

async function fetchCsrfToken(instance: AxiosInstance, csrfUrl: string): Promise<string | null> {
  if (csrfFetchInFlight) return csrfFetchInFlight;
  csrfFetchInFlight = (async () => {
    try {
      const res = await instance.get<{ csrfToken: string | null }>(csrfUrl, {
        withCredentials: true,
      });
      cachedCsrfToken = res.data?.csrfToken ?? null;
      if (!cachedCsrfToken) {
        Sentry.captureMessage("csrf_token_empty_body", { level: "warning", extra: { url: csrfUrl } });
      }
      return cachedCsrfToken;
    } catch (error) {
      Sentry.captureMessage("csrf_token_fetch_failed", {
        extra: { url: csrfUrl, error: error instanceof Error ? error.message : String(error) },
      });
      return null;
    } finally {
      csrfFetchInFlight = null;
    }
  })();
  return csrfFetchInFlight;
}

export function clearCsrfToken() {
  cachedCsrfToken = null;
}

export function createAxiosWrapper(adapter: PlatformAdapter, instance: AxiosInstance = axios) {
  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const returnedConfig: InternalAxiosRequestConfig = {
        ...config,
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          if (config.onUploadProgress && progressEvent.total) {
            const percentage = Math.floor((progressEvent.loaded * 100) / progressEvent.total);
            (config.onUploadProgress as unknown as (p: number, l: number) => void)(percentage, progressEvent.loaded);
          }
        },
      };
      const useCookieAuth = adapter.useCookieAuth ?? true;
      returnedConfig.withCredentials = useCookieAuth;

      if (adapter.getRequestHeaders) {
        const headers = await adapter.getRequestHeaders();
        Object.assign(returnedConfig.headers, headers);
      }

      const method = (returnedConfig.method || "get").toLowerCase();
      const url = returnedConfig.url || "";
      // The CSRF double-submit only exists for cookie sessions; Bearer-auth platforms
      // send no cookies, so the server skips CSRF and the token fetch is dead weight.
      if (useCookieAuth && STATE_CHANGING_METHODS.has(method) && !url.endsWith("/csrf-token")) {
        const csrfUrl = `${adapter.getBackendUrl()}/csrf-token`;
        const csrfAwaitStart = performance.now();
        const token = cachedCsrfToken ?? (await fetchCsrfToken(instance, csrfUrl));
        const csrfAwaitElapsedMs = Math.round(performance.now() - csrfAwaitStart);
        if (csrfAwaitElapsedMs >= CSRF_FETCH_SLOW_THRESHOLD_MS) {
          Sentry.captureMessage("csrf_token_await_slow", {
            level: "warning",
            extra: {
              url,
              method,
              elapsedMs: csrfAwaitElapsedMs,
              thresholdMs: CSRF_FETCH_SLOW_THRESHOLD_MS,
            },
          });
        }
        if (token) {
          returnedConfig.headers.set("X-CSRF-Token", token);
        } else if (adapter.isAuthenticated ? adapter.isAuthenticated() : true) {
          Sentry.captureMessage("csrf_header_missing_on_request", {
            level: "warning",
            extra: {
              url,
              method,
              resolvedTokenPresent: Boolean(token),
              cachedTokenPresent: Boolean(cachedCsrfToken),
              fetchInFlight: Boolean(csrfFetchInFlight),
              pathname: typeof window !== "undefined" ? window.location.pathname : undefined,
            },
          });
          const err = new Error("csrf_header_missing");
          globalThis.dispatchEvent(
            new ErrorEvent("error", { error: err, message: err.message, filename: "axiosShared.ts" }),
          );
          return Promise.reject(err);
        }
      }

      return returnedConfig;
    },
    (error) => Promise.reject(error),
  );

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = String(error?.response?.status);

      if (status === String(HTTP_STATUS.UNAUTHORIZED)) {
        const currentPath = adapter.navigation.getCurrentPath();
        if (!adapter.excludedPathsFor401?.includes(currentPath)) {
          await adapter.storage.removeItem("isLogedIn");
          await adapter.storage.removeItem("jwtToken");
          adapter.dispatch(adapter.actions.setLoggedOut());
          adapter.navigation.redirectToLogin({
            redirectUrl: currentPath,
            errorMessage: error?.response?.data?.errorMessage,
          });
        }
      }

      if (
        status === String(HTTP_STATUS.FORBIDDEN) &&
        error?.response?.data?.errorCode === ERROR_CODES.LICENCE_EXPIRED
      ) {
        console.log("licence expired");
        if (adapter.actions.setLicenceInvalid) {
          adapter.dispatch(adapter.actions.setLicenceInvalid());
        }
      }

      if (status === String(HTTP_STATUS.TOO_MANY_REQUESTS)) {
        return {
          ...(error.response ?? {}),
          data: {
            status: false,
            errorCode: errorCodes.ACCOUNT_RATE_LIMIT_EXCEEDED,
            errorMessage: error?.response?.data?.errorMessage,
            retryAfter: error?.response?.data?.retryAfter,
          },
        };
      }

      return Promise.reject(error);
    },
  );

  const typedAxios: TypedAxiosInstance = {
    post: (url, data, config) => instance.post(url, data, config),
    get: (url, config) => instance.get(url, config),
    put: (url, data, config) => instance.put(url, data, config),
    delete: (url, config) => instance.delete(url, config),
    patch: (url, data, config) => instance.patch(url, data, config),
  };

  return { instance, typedAxios };
}
